import { AlignCenterOutlined, AlignLeftOutlined, AlignRightOutlined, LinkOutlined, PictureOutlined, BgColorsOutlined, HighlightOutlined } from '@ant-design/icons';
import { JSONContent, useEditor, EditorContent, Editor as TTEditor, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import TextAlign from '@tiptap/extension-text-align'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import { Button, Col, Divider, Dropdown, Input, Row, Switch, Typography, message, Slider, ColorPicker } from 'antd';
import { defaultJsonContentTiptap, getBase64, TIPTAP_VARIABLES } from '../../utils';
import { useEffect, useState, useRef, useCallback, forwardRef } from 'react';
import Title from 'antd/es/typography/Title';
import { ItemType } from 'antd/es/menu/interface';
import { useDispatch } from 'react-redux';
import { addEditorImage, IInstitution } from '../../redux/reducer/institution';
import { AppDispatch } from '../../configureStore';
import { useMsal } from '@azure/msal-react';

interface EditorParams {
    content: JSONContent;
    title: string;
    onSave: (json: JSONContent, html?: string) => Promise<void>;
    type?: 'email' | 'webpage';
    editingChange?: (_: boolean) => void;
    disabled?: boolean;
    loading?: boolean;
    setLoading?: (_: boolean) => void;
    institution?: IInstitution;
}

const MenuBar = ({ editor, institution }: { editor: TTEditor | null; type: 'email' | 'webpage'; institution?: IInstitution }) => {
    const { accounts } = useMsal();
    const [searchTerm, setSearchTerm] = useState('');
    const dispatch = useDispatch<AppDispatch>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImageUpload = useCallback(async (file: File) => {
        if (!institution?.institutionId) {
            message.error('Institution ID is required for image upload');
            return;
        }

        const fileName = `${new Date().getTime()}-${file.name}`

        try {
            const payload = {
                input: {
                    id: institution.id,
                    institutionId: institution.institutionId,
                    file: await getBase64(file),
                    fileName,
                },
                headers: {
                    Authorization: `Bearer ${accounts[0].idToken}`,
                },
            };

            const response = await dispatch(addEditorImage(payload));

            if (response && response.url) {
                editor?.chain().focus().setImage({ src: response.url } as any).run();
                message.success('Image uploaded successfully');
            } else {
                message.error('Failed to get image URL from response');
            }
        } catch (error) {
            console.error('Image upload error:', error);
            message.error('Failed to upload image');
        }
    }, [accounts, message]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.type.startsWith('image/')) {
                handleImageUpload(file);
            } else {
                message.error('Please select an image file');
            }
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    if (!editor) {
        return null
    }

    const filteredVariables = TIPTAP_VARIABLES.filter(variable =>
        variable.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <Row gutter={[8, 8]} className='mb-1'>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        disabled={
                            !editor.can()
                                .chain()
                                .focus()
                                .toggleBold()
                                .run()
                        }
                        className={editor.isActive('bold') ? 'is-active' : ''}
                    >
                        Bold
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        disabled={
                            !editor.can()
                                .chain()
                                .focus()
                                .toggleItalic()
                                .run()
                        }
                        className={editor.isActive('italic') ? 'is-active' : ''}
                    >
                        Italic
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        disabled={
                            !editor.can()
                                .chain()
                                .focus()
                                .toggleStrike()
                                .run()
                        }
                        className={editor.isActive('strike') ? 'is-active' : ''}
                    >
                        Strike
                    </Button>
                </Col>
                <Col>
                    <Button
                        size='small'
                        onClick={() => editor.chain().focus().toggleCode().run()}
                        disabled={
                            !editor.can()
                                .chain()
                                .focus()
                                .toggleCode()
                                .run()
                        }
                        className={editor.isActive('code') ? 'is-active' : ''}
                    >
                        Code
                    </Button>
                </Col>
                <Col>
                    <Button
                        size='small'
                        onClick={() => {
                            const previousUrl = editor.getAttributes('link').href
                            const url = window.prompt('URL', previousUrl)

                            if (url === null) {
                                return
                            }

                            if (url === '') {
                                editor.chain().focus().extendMarkRange('link').unsetLink().run()
                                return
                            }

                            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
                        }}
                        className={editor.isActive('link') ? 'is-active' : ''}
                    >
                        <LinkOutlined />
                    </Button>
                </Col>
                <Col>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                    <Button
                        size='small'
                        onClick={() => {
                            if (institution && fileInputRef.current) {
                                fileInputRef.current.click();
                            } else {
                                const url = window.prompt('Enter image URL');
                                if (url) {
                                    editor?.chain().focus().setImage({ src: url } as any).run();
                                }
                            }
                        }}
                        className={editor.isActive('image') ? 'is-active' : ''}
                    >
                        <PictureOutlined />
                    </Button>
                </Col>
                <Col><Divider type='vertical' size='large' /></Col>
                <Col>
                    <Button
                        size='small'
                        onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                        className={editor?.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
                    >
                        <AlignLeftOutlined />
                    </Button>
                </Col>
                <Col>
                    <Button
                        size='small'
                        onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                        className={editor?.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
                    >
                        <AlignCenterOutlined />
                    </Button>
                </Col>
                <Col>
                    <Button
                        size='small'
                        onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                        className={editor?.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
                    >
                        <AlignRightOutlined />
                    </Button>
                </Col>
                <Col><Divider type='vertical' size='large' /></Col>
                <Col>
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: 'yellow',
                                    label: 'Yellow',
                                    onClick: () => editor.chain().focus().toggleHighlight({ color: '#fef3c7' }).run(),
                                    style: { backgroundColor: '#fef3c7' }
                                },
                                {
                                    key: 'green',
                                    label: 'Green',
                                    onClick: () => editor.chain().focus().toggleHighlight({ color: '#d1fae5' }).run(),
                                    style: { backgroundColor: '#d1fae5' }
                                },
                                {
                                    key: 'blue',
                                    label: 'Blue',
                                    onClick: () => editor.chain().focus().toggleHighlight({ color: '#dbeafe' }).run(),
                                    style: { backgroundColor: '#dbeafe' }
                                },
                                {
                                    key: 'purple',
                                    label: 'Purple',
                                    onClick: () => editor.chain().focus().toggleHighlight({ color: '#e9d5ff' }).run(),
                                    style: { backgroundColor: '#e9d5ff' }
                                },
                                {
                                    key: 'pink',
                                    label: 'Pink',
                                    onClick: () => editor.chain().focus().toggleHighlight({ color: '#fce7f3' }).run(),
                                    style: { backgroundColor: '#fce7f3' }
                                },
                                {
                                    key: 'red',
                                    label: 'Red',
                                    onClick: () => editor.chain().focus().toggleHighlight({ color: '#fee2e2' }).run(),
                                    style: { backgroundColor: '#fee2e2' }
                                },
                                {
                                    key: 'orange',
                                    label: 'Orange',
                                    onClick: () => editor.chain().focus().toggleHighlight({ color: '#fed7aa' }).run(),
                                    style: { backgroundColor: '#fed7aa' }
                                },
                                {
                                    key: 'gray',
                                    label: 'Gray',
                                    onClick: () => editor.chain().focus().toggleHighlight({ color: '#f3f4f6' }).run(),
                                    style: { backgroundColor: '#f3f4f6' }
                                },
                                { type: 'divider' },
                                {
                                    key: 'remove',
                                    label: 'Remove highlight',
                                    onClick: () => editor.chain().focus().unsetHighlight().run()
                                },
                            ]
                        }}
                    >
                        <Button
                            size='small'
                            className={editor.isActive('highlight') ? 'is-active' : ''}
                        >
                            <HighlightOutlined /> Highlight
                        </Button>
                    </Dropdown>
                </Col>
                <Col>
                    <ColorPicker
                        size="small"
                        value={editor.getAttributes('textStyle').color || '#000000'}
                        onChange={(color) => {
                            editor.chain().focus().setColor(color.toHexString()).run();
                        }}
                    >
                        <Button size='small'>
                            <BgColorsOutlined /> Text Color
                        </Button>
                    </ColorPicker>
                </Col>
                <Col><Divider type='vertical' size='large' /></Col>
                <Col>
                    <Button size='small' onClick={() => editor.chain().focus().unsetAllMarks().run()}>
                        Clear marks
                    </Button>
                </Col>
                <Col>
                    <Button size='small' onClick={() => editor.chain().focus().clearNodes().run()}>
                        Clear nodes
                    </Button>
                </Col>
            </Row>
            <Row gutter={[8, 8]} className='mb-1'>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().setParagraph().run()}
                        className={editor.isActive('paragraph') ? 'is-active' : ''}
                    >
                        Paragraph
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
                    >
                        H1
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
                    >
                        H2
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
                    >
                        H3
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
                        className={editor.isActive('heading', { level: 4 }) ? 'is-active' : ''}
                    >
                        H4
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
                        className={editor.isActive('heading', { level: 5 }) ? 'is-active' : ''}
                    >
                        H5
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
                        className={editor.isActive('heading', { level: 6 }) ? 'is-active' : ''}
                    >
                        H6
                    </Button>
                </Col>
                <Col><Divider type='vertical' size='large' /></Col>
                <Col>
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: 'search',
                                    label: (
                                        <Input
                                            placeholder="Search variables..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ marginBottom: 8 }}
                                        />
                                    ),
                                    disabled: true,
                                },
                                { type: 'divider' },
                                ...filteredVariables.map<ItemType>((variable) => ({
                                    key: variable.text,
                                    label: variable.name,
                                    onClick: () => {
                                        if (variable.name === 'Magic Link') {
                                            const url = '|magicLink|'

                                            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
                                        } else {
                                            editor.commands.insertContent(`|${variable.text}|`);
                                        }
                                        setSearchTerm('');
                                    },
                                })),
                            ],
                        }}
                        onOpenChange={(open) => {
                            if (!open) setSearchTerm('');
                        }}
                    >
                        <Button size="small">
                            Variables
                        </Button>
                    </Dropdown>
                </Col>
            </Row>
            <Row gutter={[8, 8]}>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        className={editor.isActive('bulletList') ? 'is-active' : ''}
                    >
                        Bullet list
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        className={editor.isActive('orderedList') ? 'is-active' : ''}
                    >
                        Ordered list
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        className={editor.isActive('codeBlock') ? 'is-active' : ''}
                    >
                        Code block
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        className={editor.isActive('blockquote') ? 'is-active' : ''}
                    >
                        Blockquote
                    </Button>
                </Col>
                <Col>
                    <Button size='small' onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                        Horizontal rule
                    </Button>
                </Col>
                <Col>
                    <Button size='small' onClick={() => editor.chain().focus().setHardBreak().run()}>
                        Hard break
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={
                            !editor.can()
                                .chain()
                                .focus()
                                .undo()
                                .run()
                        }
                    >
                        Undo
                    </Button>
                </Col>
                <Col>
                    <Button size='small'
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={
                            !editor.can()
                                .chain()
                                .focus()
                                .redo()
                                .run()
                        }
                    >
                        Redo
                    </Button>
                </Col>
            </Row>
        </div>
    )
}

export const ResizableImageComponent = forwardRef((props: any, _ref) => {
    const [width, setWidth] = useState(props.node.attrs.width || 400);
    const [showControls, setShowControls] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const startX = useRef(0);
    const startWidth = useRef(0);

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        startX.current = e.pageX;
        startWidth.current = width;
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;
            const diff = e.pageX - startX.current;
            const newWidth = Math.max(100, Math.min(800, startWidth.current + diff));
            setWidth(newWidth);
        };

        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                props.updateAttributes({ width });
            }
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, width, props]);

    const handleSliderChange = (value: number) => {
        setWidth(value);
        props.updateAttributes({ width: value });
    };

    return (
        <NodeViewWrapper className="tiptap-image-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
            <div 
                onMouseEnter={() => setShowControls(true)}
                onMouseLeave={() => !isDragging && setShowControls(false)}
                style={{ position: 'relative', display: 'inline-block' }}
            >
                <img 
                    {...props.node.attrs} 
                    width={width}
                    style={{ display: 'block', maxWidth: '100%', cursor: showControls ? 'pointer' : 'default' }}
                    draggable={false}
                />
                {showControls && (
                    <>
                        <div
                            style={{
                                position: 'absolute',
                                right: -5,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 10,
                                height: 30,
                                backgroundColor: '#1890ff',
                                cursor: 'ew-resize',
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseDown={handleMouseDown}
                        >
                            <div style={{ width: 2, height: 20, backgroundColor: 'white' }} />
                        </div>
                        <div
                            style={{
                                position: 'absolute',
                                bottom: -40,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 200,
                                padding: '8px',
                                backgroundColor: 'white',
                                border: '1px solid #d9d9d9',
                                borderRadius: 4,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                zIndex: 1000
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 12, minWidth: 50 }}>{width}px</span>
                                <Slider
                                    min={100}
                                    max={800}
                                    value={width}
                                    onChange={handleSliderChange}
                                    style={{ flex: 1 }}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </NodeViewWrapper>
    );
});

export const ResizableImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: 400,
                renderHTML: attributes => {
                    return {
                        width: attributes.width,
                    };
                },
            },
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(ResizableImageComponent);
    },
});

const extensions = [
    StarterKit,
    TextAlign.configure({
        types: ['heading', 'paragraph'],
    }),
    Link.configure({
        openOnClick: false,
        HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
        },
    }),
    ResizableImage.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
            class: 'tiptap-image',
        },
    }),
    TextStyle,
    Color,
    Highlight.configure({
        multicolor: true,
    }),
];


const Editor = ({
    content = defaultJsonContentTiptap,
    title,
    onSave,
    type = 'webpage',
    editingChange,
    disabled = false,
    loading = false,
    setLoading,
    institution,
}: EditorParams) => {
    const [editing, setEditing] = useState<boolean>(false);

    const editor = useEditor({
        editable: editing, // important: read-only!
        extensions,
        content,
    });

    useEffect(() => {
        if (editingChange) editingChange(editing);
    }, [editing]);

    return (
        <>
            <Row align='middle' justify='start' className='mb-2' gutter={8}>
                <Col span={6} className='flex-center-start'>
                    <Title level={5} className='mb-0-i mt-0 mr-2'>{title}</Title>
                    <Switch
                        disabled={disabled}
                        checked={editing}
                        checkedChildren='Save'
                        unCheckedChildren='Edit'
                        loading={loading}
                        onChange={(e) => {
                            if (e) { setEditing(e); editor?.setEditable(e); }
                            else {
                                if (setLoading) setLoading(true);
                                onSave(editor?.getJSON() ?? {}, editor?.getHTML()).then(() => {
                                    setEditing(e);
                                    editor?.setEditable(false);
                                    if (setLoading) setLoading(false);
                                });
                            }
                        }}
                    />
                </Col>
                <Col span={18}>
                    {!editing && (
                        <Typography.Text ellipsis={{ suffix: '...' }}>
                            {editor?.getText()}
                        </Typography.Text>
                    )}
                </Col>
            </Row>
            {editing && (
                <div className='bordered pa-1 menu-bar'>
                    <MenuBar
                        editor={editor}
                        type={type}
                        institution={institution}
                    />
                    <div className='white mx-1 br-sm'>
                        <EditorContent
                            editor={editor}
                        />
                    </div>
                </div>
            )
            }
        </>
    );
};

export default Editor;
